import { useEffect, useState } from "react";
import { LayoutDashboard, Building2, DollarSign, Bug, Megaphone, FileText, Settings, CreditCard, Flag, LogOut, RefreshCw } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/hooks/useAuth";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { UserAvatar } from "@/components/UserAvatar";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";

const PLATFORM_ITEMS = [
  { title: "Overview", url: "/admin", icon: LayoutDashboard },
  { title: "Businesses", url: "/admin/businesses", icon: Building2 },
  { title: "Revenue", url: "/admin/revenue", icon: DollarSign },
];

const MANAGE_ITEMS = [
  { title: "Bug Reports", url: "/admin/bug-reports", icon: Bug, showBadge: true },
  { title: "Broadcasts", url: "/admin/broadcasts", icon: Megaphone },
  { title: "Audit Log", url: "/admin/audit-log", icon: FileText },
];

const CONTROLS_ITEMS = [
  { title: "Platform Settings", url: "/admin/settings", icon: Settings },
  { title: "Plan Overrides", url: "/admin/plan-overrides", icon: CreditCard },
  { title: "Feature Flags", url: "/admin/feature-flags", icon: Flag },
];

export function FounderSidebar() {
  const { user, profile, signOut, refreshProfile, profileLoading } = useAuth();
  const { setOpenMobile } = useSidebar();
  const [openBugCount, setOpenBugCount] = useState<number>(0);

  useEffect(() => {
    supabase
      .rpc("get_founder_open_bug_count")
      .then(({ data }) => setOpenBugCount(Number(data ?? 0)))
      .catch(() => setOpenBugCount(0));
  }, []);

  const closeMobileSidebar = () => setOpenMobile(false);

  return (
    <Sidebar className="w-60 border-r-0">
      <div className="p-5 border-b border-sidebar-border">
        <h1 className="text-lg font-display font-bold text-sidebar-foreground">
          Review<span className="text-[#A7F0A7]" aria-hidden>Boost</span>
        </h1>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
          Admin Console
        </p>
      </div>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
            Platform
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {PLATFORM_ITEMS.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/admin"}
                      onClick={closeMobileSidebar}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                      activeClassName="bg-sidebar-accent text-primary font-semibold"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
            Manage
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {MANAGE_ITEMS.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={false}
                      onClick={closeMobileSidebar}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                      activeClassName="bg-sidebar-accent text-primary font-semibold"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1">{item.title}</span>
                      {item.showBadge && openBugCount > 0 && (
                        <Badge variant="destructive" className="text-[10px] h-5 min-w-5 px-1.5">
                          {openBugCount}
                        </Badge>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/40 uppercase text-[10px] tracking-widest">
            Controls
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {CONTROLS_ITEMS.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={false}
                      onClick={closeMobileSidebar}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors"
                      activeClassName="bg-sidebar-accent text-primary font-semibold"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className="flex items-center gap-3 mb-2">
          {profileLoading ? (
            <div className="h-8 w-8 shrink-0 rounded-full bg-sidebar-accent animate-pulse" aria-hidden />
          ) : (
            <UserAvatar size="sm" userId={user?.id} avatarUrl={profile?.avatar_url} fullName={profile?.full_name} />
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="text-xs text-sidebar-foreground truncate font-medium">
              {profile?.full_name || user?.email || "Founder"}
            </div>
            <span className="inline-flex items-center gap-1.5 mt-0.5">
              <Badge className="bg-amber-600 hover:bg-amber-600 text-white text-[10px] px-2 py-0">
                Founder
              </Badge>
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => refreshProfile()}
          className="flex items-center gap-2 text-sm text-sidebar-foreground/60 hover:text-sidebar-primary transition-colors w-full mb-1"
        >
          <RefreshCw className="h-4 w-4" />
          Retry Profile Sync
        </button>
        <button
          onClick={signOut}
          className="flex items-center gap-2 text-sm text-sidebar-foreground/60 hover:text-destructive transition-colors w-full"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </SidebarFooter>
    </Sidebar>
  );
}
